import type { PaginationState } from '@tanstack/react-table'
import { type Dispatch, type SetStateAction, useState } from 'react'

/**
 * Returns a list to its first page when what it is a list *of* changes.
 *
 * Keyed on the value the query is keyed on, not on the keystroke behind it: a
 * search settles some time after it is typed, and resetting on the keystroke
 * would re-read the old search from page one, a read the service records for
 * nothing.
 *
 * **Done while rendering, not in an effect.** An effect runs after the render
 * that changed the key has already committed, so a query keyed on both the
 * filter and the page fires once for the new filter on the old page — a page
 * that may not exist under it — and again once the effect lands. Resetting
 * during the render means React re-renders before anything is asked for, and
 * one request leaves.
 *
 * @example
 * const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 })
 * useFirstPageOnChange(settledSearch, setPagination)
 */
export const useFirstPageOnChange = (
  key: unknown,
  setPagination: Dispatch<SetStateAction<PaginationState>>,
): void => {
  const [seen, setSeen] = useState(key)
  if (!Object.is(seen, key)) {
    setSeen(key)
    setPagination((current) =>
      current.pageIndex === 0 ? current : { ...current, pageIndex: 0 },
    )
  }
}
